import { useEffect, useState } from "react";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Search, Pencil, Trash2, Phone, MapPin, X } from "lucide-react";

const empty = { name: "", phone: "", phone2: "", email: "", document: "", cep: "", address: "", neighborhood: "", city: "Brasília", notes: "" };

export default function Clients() {
  const { hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [detail, setDetail] = useState(null);
  const canCreate = hasPerm("clients.create");
  const canEdit = hasPerm("clients.edit");
  const canDelete = hasPerm("clients.delete");

  const load = async () => {
    const p = new URLSearchParams(); if (q) p.set("q", q);
    const { data } = await api.get(`/clients?${p}`); setItems(data);
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]);

  const remove = async (c) => {
    if (!window.confirm(`Desativar cliente ${c.name}?`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Cliente desativado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-3xl font-display font-extrabold tracking-tight text-white">Clientes</h1>
          <p className="text-zinc-400 mt-1">Base única de clientes · histórico de OS e vendas.</p></div>
        {canCreate && <Button data-testid="new-client-btn" onClick={() => setEdit({ ...empty })} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}><Plus size={16} className="mr-2" />Novo Cliente</Button>}
      </div>

      <div className="relative max-w-md"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, telefone, documento, bairro..." className="i pl-9" /></div>

      <div className="grid gap-2">
        {items.length === 0 && <p className="text-sm text-zinc-500">Nenhum cliente.</p>}
        {items.map((c) => (
          <div key={c.id} data-testid={`client-row-${c.id}`} className="alpha-card p-4 flex items-center gap-4">
            <span className="h-10 w-10 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center text-gold-bright font-semibold shrink-0">{c.name.charAt(0)}</span>
            <button onClick={() => setDetail(c.id)} data-testid={`client-open-${c.id}`} className="flex-1 min-w-0 text-left">
              <div className="text-white font-medium truncate">{c.name}</div>
              <div className="text-xs text-zinc-500 flex items-center gap-3 flex-wrap">
                {c.phone && <span className="inline-flex items-center gap-1"><Phone size={11} />{c.phone}</span>}
                {c.neighborhood && <span className="inline-flex items-center gap-1"><MapPin size={11} />{c.neighborhood}</span>}
              </div>
            </button>
            {canEdit && <button onClick={() => setEdit(c)} className="text-zinc-400 hover:text-gold-bright"><Pencil size={16} /></button>}
            {canDelete && <button data-testid={`client-del-${c.id}`} onClick={() => remove(c)} className="text-zinc-400 hover:text-red-400"><Trash2 size={16} /></button>}
          </div>
        ))}
      </div>

      {edit && <ClientDialog client={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {detail && <ClientDetail id={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function ClientDialog({ client, onClose, onSaved }) {
  const isNew = !client.id;
  const [f, setF] = useState({ ...empty, ...client });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const lookupCep = async () => {
    const cep = (f.cep || "").replace(/\D/g, "");
    if (cep.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const d = await r.json();
      if (!d.erro) setF((p) => ({ ...p, address: `${d.logradouro || ""}`.trim(), neighborhood: d.bairro || p.neighborhood, city: d.localidade || p.city }));
    } catch (e) { console.error("Falha ao consultar CEP", e); }
  };
  const save = async () => {
    if (!f.name) { toast.error("Nome é obrigatório."); return; }
    const payload = { name: f.name, phone: f.phone, phone2: f.phone2, email: f.email, document: f.document, cep: f.cep, address: f.address, neighborhood: f.neighborhood, city: f.city, notes: f.notes };
    try { if (isNew) await api.post("/clients", payload); else await api.put(`/clients/${client.id}`, payload); toast.success("Cliente salvo"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121217] border-gold/20 text-white max-w-2xl" data-testid="client-dialog">
        <DialogHeader><DialogTitle className="font-display">{isNew ? "Novo Cliente" : "Editar Cliente"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
          <div className="sm:col-span-2"><L t="Nome *"><Input data-testid="client-name" value={f.name} onChange={set("name")} className="i" /></L></div>
          <L t="Telefone principal"><Input data-testid="client-phone" value={f.phone} onChange={set("phone")} className="i" /></L>
          <L t="Telefone de recado"><Input data-testid="client-phone2" value={f.phone2} onChange={set("phone2")} className="i" placeholder="opcional" /></L>
          <L t="E-mail"><Input value={f.email} onChange={set("email")} className="i" /></L>
          <L t="Documento (CPF/CNPJ)"><Input value={f.document} onChange={set("document")} className="i" /></L>
          <L t="CEP"><Input value={f.cep} onChange={set("cep")} onBlur={lookupCep} className="i" placeholder="00000-000" /></L>
          <div className="sm:col-span-2"><L t="Endereço"><Input value={f.address} onChange={set("address")} className="i" /></L></div>
          <L t="Bairro"><Input data-testid="client-neighborhood" value={f.neighborhood} onChange={set("neighborhood")} className="i" /></L>
          <L t="Cidade"><Input value={f.city} onChange={set("city")} className="i" /></L>
          <div className="sm:col-span-2"><L t="Observações"><Textarea value={f.notes} onChange={set("notes")} className="i" rows={2} /></L></div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancelar</Button>
          <Button data-testid="client-save" onClick={save} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClientDetail({ id, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/clients/${id}`).then((r) => setData(r.data)).catch(() => {}); }, [id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121217] border-gold/20 text-white max-w-2xl" data-testid="client-detail">
        <DialogHeader><DialogTitle className="font-display">{data?.client?.name || "Cliente"}</DialogTitle></DialogHeader>
        {!data ? <p className="text-sm text-zinc-500">Carregando…</p> : (
          <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Info label="Telefone principal" value={data.client.phone} />
              <Info label="Telefone de recado" value={data.client.phone2} />
              <Info label="Documento" value={data.client.document} />
              <Info label="Bairro" value={data.client.neighborhood} />
              <Info label="Cidade" value={data.client.city} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Ordens de Serviço ({data.service_orders.length})</div>
              {data.service_orders.length === 0 ? <p className="text-sm text-zinc-600">Nenhuma OS.</p> :
                data.service_orders.map((o) => <div key={o.id} className="flex justify-between text-sm py-1.5 border-b border-white/5"><span className="text-zinc-300">{o.number} · {o.deviceModel || o.device?.model || "—"}</span><span className="text-zinc-500">{o.status}</span></div>)}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Vendas ({data.sales.length})</div>
              {data.sales.length === 0 ? <p className="text-sm text-zinc-600">Nenhuma venda.</p> :
                data.sales.map((s) => <div key={s.id} className="flex justify-between text-sm py-1.5 border-b border-white/5"><span className="text-zinc-300">{s.saleNumber}</span><span className="text-gold-bright">{money(s.total)}</span></div>)}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Aparelhos ({(data.device_sales || []).length})</div>
              {(data.device_sales || []).length === 0 ? <p className="text-sm text-zinc-600">Nenhum aparelho.</p> :
                data.device_sales.map((s) => <div key={s.id} className="flex justify-between text-sm py-1.5 border-b border-white/5"><span className="text-zinc-300">{s.saleNumber} · {s.brand} {s.model}</span><span className="text-gold-bright">{money(s.salePrice ?? s.total)}</span></div>)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Info = ({ label, value }) => (<div><div className="text-[11px] text-zinc-500">{label}</div><div className="text-zinc-200">{value || "—"}</div></div>);
const L = ({ t, children }) => (<div><Label className="text-xs text-zinc-400 mb-1.5 block">{t}</Label>{children}</div>);
